import { useEffect, useState } from 'react';

import { PageShell } from '../components/layout/PageShell.js';
import { DraftCard } from '../components/posts/DraftCard.js';
import { Stamp } from '../components/ui/Stamp.js';
import { useRoute } from '../lib/route.js';
import { getSavedPost, type SavedPostDetail } from '../lib/posts-client.js';
import {
  SmartApplyApiError,
  markDraftPublished,
  unmarkDraftPublished,
} from '../lib/api-client.js';

interface SavedPostDetailRouteProps {
  id: string;
}

export function SavedPostDetailRoute({ id }: SavedPostDetailRouteProps) {
  const { navigate } = useRoute();
  const [data, setData] = useState<SavedPostDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getSavedPost(id)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao carregar.');
      });
    return () => {
      alive = false;
    };
  }, [id]);

  return (
    <PageShell>
      <button
        type="button"
        onClick={() => navigate('/posts/saved')}
        className="mb-8 font-sans text-sm text-[var(--ink-secondary)] underline-offset-4 hover:underline"
      >
        ← voltar pro histórico
      </button>

      {error && <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>}

      {!data && !error && (
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          carregando...
        </p>
      )}

      {data && (
        <>
          <header className="mb-10">
            <p className="mb-4 inline-block">
              <Stamp>{data.sourceType === 'topic' ? 'tópico' : 'link/texto'}</Stamp>
            </p>
            <h1 className="font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)]">
              {data.label || 'Conjunto de drafts'}
            </h1>
            <p className="mt-2 font-mono text-xs text-[var(--ink-tertiary)]">
              gerado em {new Date(data.createdAt).toLocaleString('pt-BR')} · modelo{' '}
              {data.draftSet.modelUsed}
            </p>
            <p className="mt-3 max-w-2xl font-sans text-sm text-[var(--ink-secondary)]">
              {data.sourceType === 'topic'
                ? `Tópico: ${data.sourceInput}`
                : `Fonte: ${truncate(data.sourceInput, 200)}`}
            </p>
          </header>

          {data.draftSet.researchSummary && (
            <div
              className="card-paper mb-8 rounded-sm px-6 py-5"
              style={{ borderLeft: '3px solid var(--accent-saffron)' }}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
                síntese da pesquisa
              </p>
              <p className="mt-2 font-serif text-base text-[var(--ink-primary)]">
                {data.draftSet.researchSummary}
              </p>
              {data.draftSet.sources.length > 0 && (
                <ul className="mt-4 space-y-1 font-sans text-xs">
                  {data.draftSet.sources.map((s, i) => (
                    <li key={i}>
                      <span className="font-mono text-[var(--ink-muted)]">[{i + 1}] </span>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--accent-saffron)] underline-offset-4 hover:underline"
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <PublishMarker
            data={data}
            onUpdate={(updated) => setData((prev) => (prev ? { ...prev, ...updated } : prev))}
          />

          <div className="space-y-4">
            {data.draftSet.drafts.map((d, i) => {
              const published = data.publishedDrafts.filter((p) => p.draftId === d.id);
              return (
                <DraftCard
                  key={d.id}
                  draft={d}
                  index={i}
                  selected={data.selectedDraftId === d.id}
                  publishedAt={published[published.length - 1]?.publishedAt ?? null}
                />
              );
            })}
          </div>
        </>
      )}
    </PageShell>
  );
}

interface PublishMarkerUpdate {
  publishedAt: string | null;
  publishedDraftId: string | null;
  publishedDrafts: SavedPostDetail['publishedDrafts'];
}

function PublishMarker({
  data,
  onUpdate,
}: {
  data: SavedPostDetail;
  onUpdate: (updated: PublishMarkerUpdate) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [chosenId, setChosenId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Quais drafts ainda não foram publicados (pra sugerir no select). Os
  // publicados continuam disponíveis na lista — user pode re-publicar
  // o mesmo draft (republish faz sentido).
  const publishedDraftIds = new Set(
    data.publishedDrafts.map((p) => p.draftId).filter(Boolean) as string[],
  );

  async function handleMark() {
    setError(null);
    setBusy(true);
    try {
      const res = await markDraftPublished({
        draftSetId: data.id,
        draftId: chosenId || null,
      });
      onUpdate({
        publishedAt: res.publishedAt,
        publishedDraftId: res.publishedDraftId,
        publishedDrafts: res.publishedDrafts,
      });
      setChosenId('');
    } catch (e) {
      setError(e instanceof SmartApplyApiError ? e.message : 'Falha ao marcar.');
    } finally {
      setBusy(false);
    }
  }

  async function handleUnmark(publishedAt: string, draftId: string | null) {
    setError(null);
    setBusy(true);
    try {
      const res = await unmarkDraftPublished({
        draftSetId: data.id,
        draftId,
        publishedAt,
      });
      onUpdate({
        publishedAt: res.publishedAt,
        publishedDraftId: res.publishedDraftId,
        publishedDrafts: res.publishedDrafts,
      });
    } catch (e) {
      setError(e instanceof SmartApplyApiError ? e.message : 'Falha ao desfazer.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="card-paper mb-8 rounded-sm px-6 py-4"
      style={{
        borderLeft: `3px solid ${
          data.publishedDrafts.length > 0 ? 'var(--accent-forest)' : 'var(--ink-tertiary)'
        }`,
      }}
    >
      {data.publishedDrafts.length > 0 ? (
        <>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-forest)]">
            histórico de publicações ({data.publishedDrafts.length})
          </p>
          <ul className="mt-3 space-y-1">
            {[...data.publishedDrafts]
              .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
              .map((p, i) => {
                const draft = p.draftId
                  ? data.draftSet.drafts.find((d) => d.id === p.draftId)
                  : null;
                return (
                  <li
                    key={`${p.publishedAt}-${p.draftId ?? 'none'}-${i}`}
                    className="flex flex-wrap items-baseline gap-2 font-sans text-sm text-[var(--ink-secondary)]"
                  >
                    <span className="font-mono text-[var(--accent-forest)]">✓</span>
                    {draft ? (
                      <span>
                        <span className="font-mono text-xs text-[var(--ink-tertiary)]">{draft.id}</span>
                        {' — '}
                        <span className="italic">
                          {draft.text.slice(0, 60).replace(/\s+/g, ' ')}…
                        </span>
                      </span>
                    ) : (
                      <span className="italic text-[var(--ink-tertiary)]">
                        (sem draft específico)
                      </span>
                    )}
                    <span className="font-mono text-xs tabular-nums text-[var(--ink-tertiary)]">
                      em {new Date(p.publishedAt).toLocaleString('pt-BR')}
                    </span>
                    <button
                      type="button"
                      onClick={() => void handleUnmark(p.publishedAt, p.draftId)}
                      disabled={busy}
                      className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--accent-crimson)] disabled:opacity-30"
                    >
                      desfazer
                    </button>
                  </li>
                );
              })}
          </ul>
          <div className="mt-4 border-t border-[var(--border-subtle)] pt-4">
            <p className="font-sans text-xs italic text-[var(--ink-tertiary)]">
              Publicou outro draft do mesmo set? Marque aqui também (conta pra meta semanal em{' '}
              <a href="#/cadencia" className="text-[var(--accent-saffron)] underline-offset-4 hover:underline">
                cadência
              </a>
              ).
            </p>
          </div>
        </>
      ) : (
        <>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            já publicou no LinkedIn?
          </p>
          <p className="mt-2 font-sans text-sm text-[var(--ink-secondary)]">
            Marcar aqui conta pra sua meta semanal em{' '}
            <a href="#/cadencia" className="text-[var(--accent-saffron)] underline-offset-4 hover:underline">
              cadência
            </a>
            . Você pode marcar vários drafts do mesmo set ao longo do tempo (1 por dia, etc).
          </p>
        </>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <select
          value={chosenId}
          onChange={(e) => setChosenId(e.target.value)}
          disabled={busy}
          className="rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-sm text-[var(--ink-primary)] disabled:opacity-50"
        >
          <option value="">qual draft? (opcional)</option>
          {data.draftSet.drafts.map((d) => (
            <option key={d.id} value={d.id}>
              {publishedDraftIds.has(d.id) ? '↻ ' : ''}
              {d.id} — {d.text.slice(0, 50).replace(/\s+/g, ' ')}…
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void handleMark()}
          disabled={busy}
          className="rounded-sm bg-[var(--accent-forest)] px-4 py-2 font-sans text-sm text-[var(--bg-paper)] hover:opacity-90 disabled:opacity-40"
        >
          {busy ? '...' : 'Marcar como publicado'}
        </button>
      </div>
      {error && <p className="mt-2 font-sans text-xs text-[var(--accent-crimson)]">{error}</p>}
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}...` : s;
}
